import { RoutesList } from './routes-list';

export type RoutePermission = string | readonly string[];

export function hasRequiredPermissions(
  required: RoutePermission,
  hasPermission: (permission: string) => boolean,
): boolean {
  return (Array.isArray(required) ? required : [required]).every(hasPermission);
}

/**
 * Maps route paths to the permission string required to access them.
 * Routes NOT listed here (HOME, LOGIN, REGISTER, LOGOUT) require no permission.
 */
export const ROUTE_PERMISSION_MAP: Record<string, RoutePermission> = {
  [RoutesList.SELF_CHECK_TEMPERATURE]: 'TEMPERATURE',
  [RoutesList.SELF_CHECK_CLEANING]: 'CLEANING',
  [RoutesList.CLEANING_AREAS]: 'CLEANING',
  [RoutesList.SELF_CHECK_PEST_CONTROL]: 'PEST_CONTROL',
  [RoutesList.SELF_CHECK_METAL_DETECTION]: 'METAL_DETECTION',
  [RoutesList.SELF_CHECK_METAL_DETECTION_SUMMARY]: 'METAL_DETECTION',
  [RoutesList.SELF_CHECK_METAL_DETECTION_EXPORT_PDF]: 'METAL_DETECTION',
  [RoutesList.SELF_CHECK_METAL_DETECTION_DETECTOR_HISTORY]: 'METAL_DETECTION',
  [RoutesList.SELF_CHECK_OIL_CHANGE]: 'OIL_CHANGE',
  [RoutesList.SELF_CHECK_BLAST_CHILLING]: 'BLAST_CHILLING',
  [RoutesList.SELF_CHECK_COOKING_TEMPERATURE]: 'COOKING_TEMPERATURE',
  [RoutesList.GOODS_RECEIVING]: 'GOODS_RECEIVING',
  [RoutesList.LOTS]: 'LOTS',
  [RoutesList.RECALLS]: 'RECALL',
  [RoutesList.RECIPES]: 'RECIPES',
  [RoutesList.LABELS]: ['NUTRITIONAL_LABELS', 'RECIPES'],
  [RoutesList.LABEL_RECIPE]: ['NUTRITIONAL_LABELS', 'RECIPES'],
  [RoutesList.LABEL_RECIPE_STEP]: ['NUTRITIONAL_LABELS', 'RECIPES'],
  [RoutesList.LABEL_LOT]: ['NUTRITIONAL_LABELS', 'RECIPES'],
  [RoutesList.INGREDIENTS]: 'RECIPES',
  [RoutesList.EQUIPMENT]: 'EQUIPMENT',
  [RoutesList.AMBIENT_REGISTRY]: 'EQUIPMENT',
  [RoutesList.SUPPLIERS]: 'SUPPLIERS',
  [RoutesList.CUSTOMERS]: 'CUSTOMERS',
  [RoutesList.TRANSPORT_DOCUMENTS]: 'CUSTOMERS',
  [RoutesList.TRANSPORT_DOCUMENTS_CREATE]: 'CUSTOMERS',
  [RoutesList.DOCUMENTS]: 'DOCUMENTS',
  [RoutesList.ARCHIVE_GOODS_RECEIVING_PHOTOS]: 'GOODS_RECEIVING',
  // Static prefix (not the `:templateId` route literal): MainLayout resolves
  // route permissions by exact match then `pathname.startsWith(key)`, and a real
  // path `/doc-generation/<uuid>` never matches the parametric literal. Keying on
  // the static prefix is what makes the DOCUMENT_GENERATION gate actually fire.
  [RoutesList.DOC_GENERATION]: 'DOCUMENT_GENERATION',
  [RoutesList.SETTINGS]: 'SETTINGS',
  [RoutesList.HACCP]: 'DASHBOARD',
  [RoutesList.INSPECTION_ACCESS]: 'INSPECTION_ACCESS',
  [RoutesList.TEAM]: 'USER_MANAGEMENT',
  [RoutesList.BILLING]: 'BILLING',
  [RoutesList.EMAIL_PREFERENCES]: 'SETTINGS',
  [RoutesList.CLOSING_DAYS]: 'SETTINGS',
  [RoutesList.LABEL_FORMAT]: 'SETTINGS',
  [RoutesList.COMPANY_PROFILE]: 'SETTINGS',
  // Gated by EQUIPMENT, not SETTINGS: the backend food-preset endpoints are
  // under Permission.EQUIPMENT (spec §7) — gating the route by SETTINGS would
  // let a user open the page and then get 403s on every call.
  [RoutesList.FOOD_PRESETS]: 'EQUIPMENT',
  [RoutesList.SURVEYS]: 'SURVEYS',
  [RoutesList.SURVEY_RESPONSE_DETAIL]: 'SURVEYS',
  [RoutesList.SURVEY_RUNNER]: 'SURVEYS',
  [RoutesList.SURVEY_RUNNER_STEP]: 'SURVEYS',
  [RoutesList.MENUS]: 'RECIPES',
  [RoutesList.MENUS_DETAIL]: 'RECIPES',
};
